import React, { useMemo, useState } from 'react';
import {
  Modal,
  Box,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
  Text,
  Textarea,
  TextInput,
} from '@televet/kibble-ui';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { useGrantPointsMutation } from 'shared/types/graphql';
import isEmpty from 'lodash-es/isEmpty';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';

interface AddPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  petParentName: string;
  accountId: string;
  isWaitingForUpdate: boolean;
  setIsWaitingForUpdate: React.Dispatch<React.SetStateAction<boolean>>;
}

const MAX_CHAR_COUNT = 100;

interface PointGranter {
  points: string;
  internalNotes: string;
  publicNotes: string;
}

enum Validation {
  NotEmptyAndWholeNumber = 'notEmptyAndWholeNumber',
  MaxChar = 'maxChar',
}

enum ValidationField {
  Points = 'points',
  PublicNotes = 'publicNotes',
}

type Errors = {
  [key in ValidationField]?: boolean;
};

export const AddPointsModal = ({
  isOpen,
  onClose,
  petParentName,
  accountId,
  isWaitingForUpdate,
  setIsWaitingForUpdate,
}: AddPointsModalProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const { trackEvent } = useTracking();
  const [errors, setErrors] = useState<Errors>({});
  const [pointGranter, setPointGranter] = useState<PointGranter>({ points: '', internalNotes: '', publicNotes: '' });

  const handleValidation = (field: ValidationField, value: string, validation: Validation): void => {
    setErrors({ ...errors, [field]: !isValid(validation, value) });
  };

  const [addPoints, { loading: isAddingPoints }] = useGrantPointsMutation();

  const showErrorToast = (): void => {
    setIsWaitingForUpdate(false);
    toastMessagePop(
      `Whoops! There was a problem adding points, please refresh the page and try again.`,
      STATUSES.error,
    );
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    const validations = {
      [ValidationField.Points]: !isValid(Validation.NotEmptyAndWholeNumber, pointGranter.points),
      [ValidationField.PublicNotes]: !isValid(Validation.MaxChar, pointGranter.publicNotes),
    };
    setErrors({ ...validations });
    if (Object.values(validations).every((error) => !error)) {
      try {
        setIsWaitingForUpdate(true);
        const response = await addPoints({
          variables: {
            data: {
              loyaltyAccountId: accountId,
              points: Number(pointGranter.points),
              internalNotes: pointGranter.internalNotes,
              publicNotes: pointGranter.publicNotes,
            },
          },
        });

        trackEvent(LoyaltyEvents.EarnVirtualCurrency, { points: pointGranter.points, cause: 'manual' });

        if (!response.data?.submitLoyaltyPointGrantToAccountByUser.success) {
          showErrorToast();
        }
      } catch {
        showErrorToast();
      }
    }
  };

  const isInvalid = useMemo(() => (isEmpty(errors) ? true : Object.values(errors).some((error) => !!error)), [errors]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" allowEscape={false}>
      <ModalCloseButton />
      <ModalHeader>Add Points for {petParentName}</ModalHeader>
      <ModalBody pt={4}>
        <Box mb={6}>
          <Flex alignItems="center">
            <Text as="p" fontWeight="bold">
              Points to Add <Text variant="danger">*</Text>
            </Text>
            <TextInput
              name="points"
              type="number"
              isRequired
              ml={3}
              value={pointGranter.points}
              onChange={(e): void => {
                setPointGranter({ ...pointGranter, points: e.target.value });
                handleValidation(ValidationField.Points, e.target.value, Validation.NotEmptyAndWholeNumber);
              }}
              width="80px"
              focusBorderColor="border.focus"
              isInvalid={errors[ValidationField.Points]}
            />
          </Flex>
          {errors['points'] && (
            <Text as="p" size="xs" variant="danger" ml="126px" mt={2}>
              Field can&apos;t be empty and must be an integer
            </Text>
          )}
        </Box>
        <Box mb={6}>
          <Textarea
            name="publicNotes"
            helperText={`${pointGranter.publicNotes.length}/${MAX_CHAR_COUNT}`}
            label="Add a Note for Pet Parent(s)"
            labelStyle={{ fontWeight: 'bold' }}
            value={pointGranter.publicNotes}
            onChange={(e): void => {
              setPointGranter({ ...pointGranter, publicNotes: e.target.value });
              handleValidation(ValidationField.PublicNotes, e.target.value, Validation.MaxChar);
            }}
            errorText="Field can't be above 100 characters"
            isInvalid={errors[ValidationField.PublicNotes]}
          />
        </Box>
        <Box mb={6}>
          <Textarea
            name="internalNotes"
            label="Internal Clinic Note Only"
            labelStyle={{ fontWeight: 'bold' }}
            value={pointGranter.internalNotes}
            onChange={(e): void => {
              setPointGranter({ ...pointGranter, internalNotes: e.target.value });
            }}
          />
        </Box>
      </ModalBody>
      <ModalFooterButtons
        submitText="Save"
        onSubmit={handleSubmit}
        isLoading={isAddingPoints || isWaitingForUpdate}
        isDisabled={isInvalid || isAddingPoints || isWaitingForUpdate}
      />
    </Modal>
  );
};

const isValid = (validation: Validation, value: string): boolean => {
  switch (validation) {
    case Validation.NotEmptyAndWholeNumber:
      const valueAsNumber = Number(value);
      return valueAsNumber > 0 && valueAsNumber % 1 === 0;
    case Validation.MaxChar:
      return value.length <= MAX_CHAR_COUNT;
    default:
      return false;
  }
};
